import type { FC, ReactNode } from 'react';
import PropTypes from 'prop-types';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';

import { SideNavItem } from './side-nav-item';
import { useTheme } from '@mui/material/styles';

interface Item {
  disabled?: boolean;
  external?: boolean;
  icon?: ReactNode;
  items?: Item[];
  label?: ReactNode;
  path?: string;
  title: string;
}

const renderItems = ({
  depth = 0,
  items,
  pathname,
  open,
}: {
  depth?: number;
  items: Item[];
  pathname?: string | null;
  open?: boolean;
}): JSX.Element[] =>
  items.reduce(
    (acc: JSX.Element[], item) =>
      reduceChildRoutes({
        acc,
        depth,
        item,
        pathname,
        open,
      }),
    []
  );

const reduceChildRoutes = ({
  acc,
  depth,
  item,
  pathname,
  open,
}: {
  acc: JSX.Element[];
  depth: number;
  item: Item;
  pathname?: string | null;
  open?: boolean;
}): Array<JSX.Element> => {
  const checkPath = !!(item?.path && pathname);
  const partialMatch = checkPath ? pathname.includes(item.path || '') : false;
  const pathParts = pathname?.split('/');
  const isSecondLevel = pathParts?.length === 2;
  const exactMatch =
    checkPath &&
    // A nested path such as /branded-app/merchant-locations collapses to
    // "branded-appmerchant-locations" below and can never match, so compare it
    // outright first -- which is all the mobile nav has ever done.
    (pathname === item.path ||
      (pathParts && pathParts[1] === item?.path?.replace(/\//g, '').toLowerCase()) ||
      (isSecondLevel && pathParts[2] === item.title.toLowerCase()));

  if (item.items) {
    acc.push(
      <SideNavItem
        active={partialMatch}
        depth={depth}
        disabled={item.disabled}
        icon={item.icon}
        key={item.title}
        label={item.label}
        open={partialMatch}
        title={open ? item.title : ''}
      >
        <Stack
          component="ul"
          spacing={0.5}
          sx={{
            listStyle: 'none',
            m: 0,
            p: 0,
          }}
        >
          {renderItems({
            depth: depth + 1,
            items: item.items,
            pathname,
            open: open,
          })}
        </Stack>
      </SideNavItem>
    );
  } else {
    acc.push(
      <SideNavItem
        active={exactMatch}
        depth={depth}
        disabled={item.disabled}
        external={item.external}
        icon={item.icon}
        key={item.title}
        label={item.label}
        path={item.path}
        title={open ? item.title : ''}
      />
    );
  }

  return acc;
};

interface SideNavSectionProps {
  items?: Item[];
  pathname?: string | null;
  subheader?: string;
  open: boolean;
}

export const SideNavSection: FC<SideNavSectionProps> = (props) => {
  const { items = [], pathname, subheader = '', open, ...other } = props;
  const theme = useTheme();
  return (
    <Stack
      component="ul"
      spacing={0.5}
      sx={{
        listStyle: 'none',
        m: 0,
        p: 0,
      }}
      {...other}
    >
      {subheader && (
        <Box
          component="li"
          sx={{
            color: theme.palette.text.secondary,
            fontSize: 12,
            fontWeight: 700,
            lineHeight: 1.66,
            mb: 1,
            ml: 1,
            textTransform: 'uppercase',
          }}
        >
          {subheader}
        </Box>
      )}
      {renderItems({ items, pathname, open })}
    </Stack>
  );
};

SideNavSection.propTypes = {
  items: PropTypes.array,
  pathname: PropTypes.string,
  subheader: PropTypes.string,
};
